import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import Sticky from 'react-stickynode';

export interface TabsScrollableProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: React.ReactNode | any;
  selected?: number;
  size: 'small' | 'large';
  tabsScrollableTop?: number;
  scrollToIndex?: null | number;
}

export const TabsScrollable = ({
  children,
  selected: defaultSelected,
  size,
  tabsScrollableTop,
  scrollToIndex,
}: TabsScrollableProps): JSX.Element => {
  const [selected, setSelected] = useState<number>(defaultSelected || 0);
  const handleChange = (index: number): void => {
    scrollToSection(index);
  };

  const childrenObserver: {
    title: string;
    inView: boolean;
    ref: React.MutableRefObject<HTMLDivElement>;
  }[] = [];

  const THRESHOLD = new Array(React.Children.count).map((_, index) => {
    if (index === 0) return 0.5;
    return 1;
  });

  const scrollToSection = useCallback(
    (index: number) => {
      if (childrenObserver[index].ref.current) {
        childrenObserver[index].ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    },
    [childrenObserver]
  );

  const childrenWithRef = React.Children.map(children, (child) => {
    const ref = React.useRef<HTMLDivElement>(child?.props.title);
    const [inViewRef, inView] = useInView({
      threshold: THRESHOLD,
    });

    const setRefs = useCallback(
      (node) => {
        ref.current = node;
        inViewRef(node);
      },
      [inViewRef]
    );

    childrenObserver.push({
      title: child?.props.title,
      ref,
      inView,
    });
    return <div ref={setRefs}>{child}</div>;
  });

  useEffect(() => {
    childrenObserver.forEach(({ inView }, index) => {
      if (inView) {
        setSelected(index);
      }
    });
  }, [childrenObserver]);

  useEffect(() => {
    if (scrollToIndex) {
      handleChange(scrollToIndex);
    }
  }, [scrollToIndex]);

  return (
    <>
      <Sticky enabled={true} top={tabsScrollableTop} innerZ={10}>
        <div className="flex top-0 pt-12 border-b-2 border-gray-300 bg-white">
          <div className="inline-flex pl-120">
            {React.Children.map(children, (child, index) => {
              const className = classNames({
                'font-medium cursor-pointer mr-26 text-gray-500 px-2 -mb-2 pb-16': true,
                'text-primary-500 border-primary-500 border-b-2 ': index === selected,
                'text-sm': size === 'small',
              });
              return (
                <button className={className} key={index} onClick={handleChange.bind(this, index)}>
                  {child?.props.title}
                </button>
              );
            })}
          </div>
        </div>
      </Sticky>
      <div className="pl-120 lg:max-w-6xl md:max-w-4xl sm:max-w-3xl max-w-2xl">
        {childrenWithRef}
      </div>
    </>
  );
};
